import { Fragment, useCallback } from 'react'
import { Conversation, ConversationContent } from '@/components/ai/conversation'
import { cn } from '@/lib/utils'
import type { ClientMessage } from '../types'
import {
  MessageFailureDetails,
  MessageStatusLabel,
  messageBodyText,
} from './MessageStatus'

function formatBubbleTime(date: Date): string {
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}
function formatDayHeading(date: Date, today: Date, yesterday: Date): string {
  if (Number.isNaN(date.getTime())) return 'Unknown date'
  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return date.toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

interface MessageBubblesProps {
  messages: ClientMessage[]
  selectedMessageId?: string
}

/**
 * Renders a conversation as iMessage-style bubbles: outbound (from us) on the
 * right, inbound (from the client) on the left. The selected message is brought
 * into view; other callers keep the shared container's stick-to-latest behavior.
 */
export function MessageBubbles({
  messages,
  selectedMessageId,
}: MessageBubblesProps) {
  const selectedRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const frame = requestAnimationFrame(() => {
      node.scrollIntoView?.({ block: 'center', behavior: 'instant' })
    })
    return () => cancelAnimationFrame(frame)
  }, [])
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const content = messages.map((message, index) => {
    const isOutbound = message.direction === 'outbound'
    const selected = message.id === selectedMessageId
    const date = new Date(message.created_at)
    const previous = messages[index - 1]
    const startsDay =
      !previous ||
      new Date(previous.created_at).toDateString() !== date.toDateString()
    return (
      <Fragment key={message.id}>
        {startsDay ? (
          <h3 className="text-muted-foreground px-4 py-3 text-center text-xs font-medium">
            {formatDayHeading(date, today, yesterday)}
          </h3>
        ) : null}
        <div
          ref={selected ? selectedRef : undefined}
          role="group"
          aria-label={selected ? 'Selected message' : undefined}
          aria-current={selected ? 'true' : undefined}
          className={cn(
            'flex w-full min-w-0 flex-col gap-0.5 px-4 py-2',
            isOutbound ? 'items-end' : 'items-start',
            // Mirror the timestamp line above the bubble, so its surrounding
            // highlight has the same visual height on both sides.
            selected &&
              "border-primary-solid my-2 border-y bg-primary-active-bg/50 before:h-4 before:shrink-0 before:content-['']"
          )}>
          <div
            className={cn(
              'max-w-[75%] rounded-2xl px-3 py-2 text-sm break-words whitespace-pre-wrap',
              isOutbound
                ? 'bg-primary text-primary-foreground rounded-br-sm'
                : 'bg-gray-200 text-gray-900 rounded-bl-sm dark:bg-gray-700 dark:text-gray-50'
            )}>
            {messageBodyText(message)}
          </div>
          <div className="text-muted-foreground flex items-center gap-1 px-1 text-[10px] leading-4">
            <MessageStatusLabel message={message} />
            <span aria-hidden="true">{' · '}</span>
            <time
              dateTime={message.created_at}
              className="text-[10px] leading-4 whitespace-nowrap">
              {formatBubbleTime(date)}
            </time>
          </div>
          <MessageFailureDetails message={message} />
        </div>
      </Fragment>
    )
  })
  if (selectedMessageId) {
    return (
      <div
        className="size-full overflow-y-auto"
        role="log"
        aria-label="Conversation history">
        <div className="flex flex-col gap-1 py-4">{content}</div>
      </div>
    )
  }
  return (
    <Conversation className="size-full">
      <ConversationContent className="gap-1 px-0 py-4">
        {content}
      </ConversationContent>
    </Conversation>
  )
}
